import { SupplierPollOutcome, SupplierPollOutcomeLabels } from '@navio/nsdk';
import { NoddiIcon, type NoddiIconName } from 'noddi-ui-common';
import { BadgeVariant, NoddiBadge } from 'noddi-ui-web';

const ERROR_TEXT_OUTCOMES = new Set<SupplierPollOutcome>([
  SupplierPollOutcome.DOCUMENT_INVALID,
  SupplierPollOutcome.SUPPLIER_ERROR
]);

const SUPPLIER_POLL_BADGE: Record<SupplierPollOutcome, { icon: NoddiIconName; variant: BadgeVariant }> = {
  [SupplierPollOutcome.IN_PROGRESS]: { icon: 'Activity', variant: 'info' },
  [SupplierPollOutcome.INVOICED]: { icon: 'ReceiptText', variant: 'warning' },
  [SupplierPollOutcome.NOT_VISIBLE_YET]: { icon: 'EyeOff', variant: 'outline' },
  [SupplierPollOutcome.DOCUMENT_INVALID]: { icon: 'TriangleAlert', variant: 'destructive' },
  [SupplierPollOutcome.CREDENTIALS_REJECTED]: { icon: 'Lock', variant: 'destructive' },
  [SupplierPollOutcome.TEMPORARILY_UNAVAILABLE]: { icon: 'Clock', variant: 'outline' },
  [SupplierPollOutcome.SUPPLIER_ERROR]: { icon: 'CircleX', variant: 'destructive' },
  [SupplierPollOutcome.CANCELLED]: { icon: 'XCircle', variant: 'destructive' }
};

interface PollOrder {
  supplierPollOutcome?: SupplierPollOutcome | null;
  supplierPollAction?: string;
  supplierErrorText?: string;
}

interface Props {
  order: PollOrder;
  showAction?: boolean;
}

export const SupplierPollOutcomeBadge = ({ order, showAction = false }: Props) => {
  const outcome = order.supplierPollOutcome;
  if (!outcome) {
    return null;
  }

  const style = SUPPLIER_POLL_BADGE[outcome];
  const label = SupplierPollOutcomeLabels[outcome] ?? outcome;
  const badge = (
    <NoddiBadge variant={style.variant}>
      <NoddiIcon name={style.icon} size='small' />
      {label}
    </NoddiBadge>
  );

  if (!showAction) {
    return badge;
  }

  const errorText = order.supplierErrorText;
  const visibleError = errorText && ERROR_TEXT_OUTCOMES.has(outcome) && errorText !== label ? errorText : '';

  return (
    <div className='flex flex-col gap-1'>
      <div>{badge}</div>
      {order.supplierPollAction ? <p className='text-sm'>{order.supplierPollAction}</p> : null}
      {visibleError ? <p className='text-xs text-muted-foreground'>{visibleError}</p> : null}
    </div>
  );
};
